import type { AxiosInstance } from "axios";
import { Building2, LayoutGrid, LayoutDashboard, LogOut, ScrollText, ShieldCheck, Store, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "../lib/cn.js";
import type { AdminUser } from "../lib/admin.js";
import { OverviewScreen } from "./OverviewScreen.js";
import { OrgsScreen } from "./OrgsScreen.js";
import { OrgDetailScreen } from "./OrgDetailScreen.js";
import { PlansScreen } from "./PlansScreen.js";
import { AuditScreen } from "./AuditScreen.js";
import { StoresScreen } from "./StoresScreen.js";
import { StaffScreen } from "./StaffScreen.js";
import { Logo } from "../components/Logo.js";

/**
 * The signed-in portal frame.
 *
 * THE SIDEBAR IS FIXED — `sticky top-0 h-svh` with its OWN overflow — so it stays put while
 * the page scrolls. An earlier version made it a plain grid column, which scrolled away with
 * the content; on a long org list the nav simply left the screen.
 *
 * The content column follows the merchant app's language: the tinted page (`bg-(--page)`), a
 * slim sticky top bar, and full-width content in card panels. Nothing is centred in a narrow
 * column — a data portal wants the width.
 */

interface Route {
  readonly section: "overview" | "orgs" | "stores" | "plans" | "audit" | "staff";
  readonly orgId: string | null;
}

const UUID = /^[0-9a-f-]{36}$/i;

function parse(pathname: string): Route {
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/");
  if (parts[0] === "overview" || parts[0] === "") return { section: "overview", orgId: null };
  if (parts[0] === "stores") return { section: "stores", orgId: null };
  if (parts[0] === "plans") return { section: "plans", orgId: null };
  if (parts[0] === "audit") return { section: "audit", orgId: null };
  if (parts[0] === "staff") return { section: "staff", orgId: null };
  if (parts[0] === "orgs" && parts[1] !== undefined && UUID.test(parts[1])) {
    return { section: "orgs", orgId: parts[1] };
  }
  if (parts[0] === "orgs") return { section: "orgs", orgId: null };
  return { section: "overview", orgId: null };
}

const NAV: readonly { readonly section: Route["section"]; readonly label: string; readonly icon: LucideIcon; readonly to: string }[] = [
  { section: "overview", label: "Overview", icon: LayoutDashboard, to: "/overview" },
  { section: "orgs", label: "Organizations", icon: Building2, to: "/orgs" },
  { section: "stores", label: "Stores", icon: Store, to: "/stores" },
  { section: "plans", label: "Plans", icon: LayoutGrid, to: "/plans" },
  { section: "audit", label: "Audit", icon: ScrollText, to: "/audit" },
  { section: "staff", label: "Staff", icon: ShieldCheck, to: "/staff" },
];

const SECTION_TITLE: Readonly<Record<Route["section"], string>> = {
  overview: "Overview",
  orgs: "Organizations",
  stores: "Stores",
  plans: "Plans",
  audit: "Audit",
  staff: "Staff",
};

export function AdminShell({
  client,
  admin,
  appEnv,
  onSignOut,
}: {
  readonly client: AxiosInstance;
  readonly admin: AdminUser;
  readonly appEnv: string;
  readonly onSignOut: () => Promise<void>;
}): React.JSX.Element {
  const [route, setRoute] = useState<Route>(() => parse(window.location.pathname));

  useEffect(() => {
    const onPop = (): void => setRoute(parse(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = (to: string): void => {
    window.history.pushState(null, "", to);
    setRoute(parse(to));
  };

  return (
    <div className="bg-(--page) flex min-h-svh">
      {/* FIXED sidebar: sticky, full viewport height, its own scroll. */}
      <aside className="bg-card sticky top-0 flex h-svh w-60 shrink-0 flex-col border-r">
        <div className="flex h-14 items-center gap-2 border-b px-4">
          <Logo className="h-6 w-auto" />
          <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-slate-200 uppercase">
            Admin
          </span>
        </div>

        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
          {NAV.map((item) => {
            const active = route.section === item.section;
            const Icon = item.icon;
            return (
              <a
                key={item.section}
                href={item.to}
                aria-current={active ? "page" : undefined}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                  event.preventDefault();
                  navigate(item.to);
                }}
                className={cn(
                  "flex h-10 items-center gap-2.5 rounded-lg px-3 text-sm font-medium transition-colors",
                  active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-accent",
                )}
              >
                <Icon className="size-4 shrink-0" />
                {item.label}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 border-t p-3">
          {/*
            Defensive: an older API's /admin/me returns only { id }, so name and email may
            be missing at runtime even though the type says otherwise. Fall back through
            name → email → id so the avatar and labels never read `undefined.trim()`.
          */}
          <div className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
            {(admin.name?.trim() || admin.email || admin.id).slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium">{admin.name || admin.email || "Staff"}</p>
            <p className="text-muted-foreground truncate text-[11px]">{admin.email || admin.id}</p>
          </div>
          <button
            type="button"
            aria-label="Sign out"
            onClick={() => void onSignOut()}
            className="text-muted-foreground hover:bg-accent hover:text-foreground inline-flex size-8 items-center justify-center rounded-md transition-colors"
          >
            <LogOut className="size-4" />
          </button>
        </div>
      </aside>

      {/* Content column: full width, its own sticky top bar. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="bg-card/80 sticky top-0 z-10 flex h-14 items-center gap-3 border-b px-6 backdrop-blur">
          <h2 className="text-sm font-semibold">{SECTION_TITLE[route.section]}</h2>
          <div className="flex-1" />
          <span className="text-muted-foreground rounded-md border px-2 py-0.5 text-xs">{appEnv}</span>
        </header>

        <main className="min-w-0 flex-1 px-6 py-6">
          {route.section === "overview" && <OverviewScreen client={client} />}
          {route.section === "orgs" && route.orgId === null && <OrgsScreen client={client} onOpenOrg={(id) => navigate(`/orgs/${id}`)} />}
          {route.section === "orgs" && route.orgId !== null && (
            <OrgDetailScreen key={route.orgId} client={client} orgId={route.orgId} onBack={() => navigate("/orgs")} />
          )}
          {route.section === "stores" && <StoresScreen client={client} />}
          {route.section === "plans" && <PlansScreen client={client} />}
          {route.section === "audit" && <AuditScreen client={client} />}
          {route.section === "staff" && <StaffScreen client={client} currentAdminId={admin.id} />}
        </main>
      </div>
    </div>
  );
}
